import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiAll, apiGet, apiPost, apiPut } from 'api-service';
import { xor } from 'lib';
import translation from 'translation';
import {
  twitterDefaultData,
  twitterDefaultWordCloudData,
} from './onboardingStaticData';
const { t } = translation;

export const updateOnboardingSettings = createAsyncThunk(
  'onboardingReducer/updateOnboardingSettings',
  async (payload, thunkAPI) => {
    try {
      const response = await apiPost('/user/user-settings', {
        key: payload.key,
        value: {
          ...payload.value,
        },
      });
      return response.data.result.data.value;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchOnboardingSettings = createAsyncThunk(
  'onboardingReducer/fetchOnboardingSettings',
  async (payload, thunkAPI) => {
    try {
      const response = await apiGet('/user/user-settings', {
        key: 'onboarding_settings',
      });
      return response.data.result.data.value;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * محاسبه متن کوئری مربوط به موضوع ساز ساده
 * مثال:
 * (money + cash)
 */
const calculateParsedQuery = (lists, conjunction = ' + ', prefix = '') => {
  if (prefix) lists = lists.map((item) => prefix + item);
  return `(${lists.join(conjunction)})`;
};

export const fetchTwitterData = createAsyncThunk(
  'onboardingReducer/fetchTwitterData',
  async (payload, thunkAPI) => {
    const onboardingState = thunkAPI.getState().onboardingReducer;
    const params = {
      ...payload,
      api_path: '/twitter/posts',
      count: 5,
      page: 1,
      source: 'original',
      [onboardingState?.queryId ? 'query' : 'q']: onboardingState?.queryId
        ? onboardingState?.queryId
        : onboardingState?.user?.name,
    };

    try {
      const response = await apiGet('/search/api/v1/search', params);

      let _twitterData = [];

      if (response.data.result.data?.length > 0) {
        response.data.result.data?.forEach((x) => {
          _twitterData.push({
            username: x.username,
            time: x.time,
            description: x.description,
            like_count: x.like_count,
            copy_count: x.copy_count,
            avatar: x.avatar,
          });
        });
      } else _twitterData = twitterDefaultData();

      return _twitterData;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchSubjects = createAsyncThunk(
  'onboardingReducer/fetchQueryList',
  async (payload, thunkAPI) => {
    const onboardingState = thunkAPI.getState().onboardingReducer;
    const fetchParams = {
      ...payload,
      no_paginate: 1,
      orderBy: [{ field: 'created_at', type: 'desc' }],
      per_page: 10,
    };
    const createParams = {
      ...payload,
      title: onboardingState?.user?.name,
      description: t('onboarding:thisQueryIsCreatedAccordingToYourInfo'),
      parsed_query: onboardingState?.user?.name,
      simple_query: JSON.stringify({
        contain: [onboardingState?.user?.name],
        must_contain: [],
        must_not_contain: [],
      }),
    };

    try {
      const response = await apiGet('/search/queries', fetchParams);
      let createSubjectResponse;

      if (response.data.result.data?.length <= 0) {
        createSubjectResponse = await apiPost('/search/queries', createParams);
      }

      return response.data.result.data?.length > 0
        ? response.data.result.data
        : [
            {
              id: createSubjectResponse.data.result.data?.id,
              simple_query: JSON.stringify({
                contain: [onboardingState?.user?.name],
                must_contain: [],
                must_not_contain: [],
              }),
            },
          ];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createNewSubject = createAsyncThunk(
  'onboardingReducer/createNewSubject',
  async (payload, thunkAPI) => {
    const onboardingState = thunkAPI.getState().onboardingReducer;
    const createParams = {
      ...payload,
      title: `${onboardingState?.user?.name} ${t('onboarding:edited')}`,
      description: t('onboarding:thisQueryIsCreatedAccordingToYourInfo'),
      parsed_query:
        calculateParsedQuery(onboardingState?.query) ??
        onboardingState?.user?.name,
      simple_query: JSON.stringify({
        contain: onboardingState.query ?? [onboardingState?.user?.name],
        must_contain: [],
        must_not_contain: [],
      }),
    };

    try {
      const createSubjectResponse = await apiPost(
        '/search/queries',
        createParams
      );

      return createSubjectResponse.data.result.data?.id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateSubject = createAsyncThunk(
  'onboardingReducer/updateSubject',
  async (payload, thunkAPI) => {
    const onboardingState = thunkAPI.getState().onboardingReducer;
    const params = {
      ...payload,
      parsed_query:
        calculateParsedQuery(onboardingState?.query) ??
        onboardingState?.user?.name,
      simple_query: JSON.stringify({
        contain: onboardingState.query,
        must_contain: [],
        must_not_contain: [],
      }),
    };

    try {
      const response = await apiPut(
        '/search/queries/:id'.replace(':id', onboardingState?.queryId),
        params
      );
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchWordCloud = createAsyncThunk(
  'onboardingReducer/wordCloud',
  async (payload, thunkAPI) => {
    const onboardingState = thunkAPI.getState().onboardingReducer;
    const platforms = ['instagram', 'telegram', 'twitter', 'news'];
    const params = {
      ...payload,
      api_path: 'subjects',
      platforms: platforms,
      time_duration: 604800, // یک هفته گذشته
      type: 'text',
      queries: [onboardingState?.queryId],
    };

    try {
      const response = await apiGet(
        '/search/api/v1/comparison/compare',
        params
      );

      const platformsData = [];

      if (
        response.data.result.data?.[onboardingState?.queryId]?.total?.length > 0
      ) {
        response.data.result.data?.[onboardingState?.queryId]?.total.forEach(
          (x) => {
            platformsData.push({
              text: x.word,
              value: x.count,
              color: '#ffffff',
            });
          }
        );
      } else {
        const _TWITTER_WORD_CLOUD = twitterDefaultWordCloudData();
        _TWITTER_WORD_CLOUD?.total.forEach((x) => {
          platformsData.push({
            text: x.word,
            value: x.count,
            color: '#ffffff',
          });
        });
      }

      return platformsData;
    } catch (error) {
      const platformsData = [];
      const _TWITTER_WORD_CLOUD = twitterDefaultWordCloudData();
      _TWITTER_WORD_CLOUD?.total.forEach((x) => {
        platformsData.push({
          text: x.word,
          value: x.count,
          color: '#ffffff',
        });
      });
      return platformsData;
    }
  }
);

export const fetchVipResources = createAsyncThunk(
  'onboardingReducer/fetchVipResources',
  async (payload, thunkAPI) => {
    try {
      const response = await apiAll([
        apiGet('/search/vip', {
          ...payload,
          resource_type: 'instagram',
          no_paginate: 1,
        }),
        apiGet('/search/vip', {
          ...payload,
          resource_type: 'twitter',
          no_paginate: 1,
        }),
        apiGet('/search/vip', {
          ...payload,
          resource_type: 'telegram',
          no_paginate: 1,
        }),
      ]);
      return {
        instagram: response[0],
        twitter: response[1],
        telegram: response[2],
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const initialState = {
  currentStep: 'step_1',
  userId: null,
  queryId: null,
  user: {},
  query: [],
  alarm: true,
  introduceAccounts: {
    instagram: [],
    telegram: [],
    twitter: [],
  },
  competitorsAccounts: {
    instagram: [],
    telegram: [],
    twitter: [],
  },
  twitterData: {
    data: [],
    loading: true,
    error: null,
  },
  wordCloud: {
    data: [],
    loading: true,
    error: null,
  },
};

export const onboardingSlice = createSlice({
  name: 'onboardingSlice',
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setQueryWords: (state, action) => {
      state.query = xor(state.query, [action?.payload?.word]);
    },
    setAlarm: (state, action) => {
      state.alarm = action.payload;
    },
    setAccounts: (state, action) => {
      const { data, platform, objectName } = action.payload;
      state[objectName][platform].push(data);
    },
    removeFromAccounts: (state, action) => {
      const { id, platform, objectName } = action.payload;
      state[objectName][platform] = state[objectName][platform].filter(
        (item) => item?.id !== id
      );
    },
    setOnboardingInitialValues: (state, action) => {
      const { user } = action.payload;
      state.user = user;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTwitterData.fulfilled, (state, action) => {
        state.twitterData.data = action.payload;
        state.twitterData.loading = false;
        state.twitterData.error = null;
      })
      .addCase(fetchTwitterData.pending, (state) => {
        state.twitterData.loading = true;
        state.twitterData.error = null;
      })
      .addCase(fetchTwitterData.rejected, (state, action) => {
        state.twitterData.loading = false;
        state.twitterData.error = action.payload;
      })
      .addCase(fetchWordCloud.fulfilled, (state, action) => {
        state.wordCloud.data = action.payload;
        state.wordCloud.loading = false;
        state.wordCloud.error = null;
      })
      .addCase(fetchWordCloud.pending, (state) => {
        state.wordCloud.loading = true;
        state.wordCloud.error = null;
      })
      .addCase(fetchWordCloud.rejected, (state, action) => {
        state.wordCloud.loading = false;
        state.wordCloud.error = action.payload;
      })
      .addCase(fetchSubjects.fulfilled, (state, action) => {
        const simple_query = JSON.parse(action.payload?.[0]?.simple_query);
        state.query = simple_query?.contain ?? [state?.user?.name];
        state.queryId = action?.payload?.[0]?.id;
      })
      .addCase(createNewSubject.fulfilled, (state, action) => {
        state.queryId = action?.payload;
      })
      .addCase(fetchOnboardingSettings.fulfilled, (state, action) => {
        state.currentStep = action.payload?.currentStep ?? state.currentStep;
        state.queryId = action.payload?.queryId ?? state.queryId;
        state.user = action.payload?.user ?? state.user;
        state.query = action.payload?.query ?? state.query;
        state.alarm = action.payload?.alarm ?? state.alarm;
        state.introduceAccounts =
          action.payload?.introduceAccounts ?? state.introduceAccounts;
        state.competitorsAccounts =
          action.payload?.competitorsAccounts ?? state.competitorsAccounts;
        state.twitterData = action.payload?.twitterData ?? state.twitterData;
        state.wordCloud = action.payload?.wordCloud ?? state.wordCloud;
      });
  },
});

export const {
  setCurrentStep,
  setQueryWords,
  setUserId,
  setAlarm,
  setAccounts,
  removeFromAccounts,
  setOnboardingInitialValues,
} = onboardingSlice.actions;
